import { IconBox } from "@/components/core/IconBox";
import { Spinner } from "@/components/core/Spinner";
import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-0.5 whitespace-nowrap rounded-medium font-['Pretendard'] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&[data-interaction=hover]]:transition-none",
    {
        variants: {
            variant: {
                filled: "",
                outlined: "bg-transparent border",
                text: "bg-transparent border-transparent",
                background: "bg-transparent border-transparent",
                "archive-button": "bg-common-white-main"
            },
            color: {
                primary: "",
                secondary: "",
                grey: ""
            },
            size: {
                xl: "px-[17px] py-3 text-base leading-6",
                large: "px-[13px] py-2 text-base leading-6",
                medium: "px-[11px] py-1 text-sm leading-6",
                small: "px-[9px] py-0.5 text-xs leading-6",
                "icon-xl": "size-12 p-0",
                "icon-large": "size-10 p-0",
                "icon-medium": "size-8 p-0",
                "icon-small": "size-7 p-0"
            }
        },
        compoundVariants: [
            // Filled variants
            {
                variant: "filled",
                color: "primary",
                className:
                    "bg-primary-main text-common-white-main hover:bg-primary-dark data-[interaction=hover]:bg-primary-dark dark:bg-primary-main dark:text-common-white-main dark:hover:bg-primary-dark dark:data-[interaction=hover]:bg-primary-dark"
            },
            {
                variant: "filled",
                color: "secondary",
                className:
                    "bg-secondary-main text-common-white-main dark:text-common-black-main hover:bg-secondary-dark data-[interaction=hover]:bg-secondary-dark dark:bg-secondary-main dark:hover:bg-secondary-dark dark:data-[interaction=hover]:bg-secondary-dark"
            },
            {
                variant: "filled",
                color: "grey",
                className:
                    "bg-grey-200 text-text-secondary hover:bg-grey-300 data-[interaction=hover]:bg-grey-300 disabled:bg-grey-200 disabled:text-text-placeholder dark:bg-grey-200 dark:text-text-secondary dark:hover:bg-grey-300 dark:data-[interaction=hover]:bg-grey-300"
            },
            // Outlined variants
            {
                variant: "outlined",
                color: "primary",
                className:
                    "border-primary-main text-primary-main hover:border-primary-dark hover:text-primary-dark data-[interaction=hover]:border-primary-dark data-[interaction=hover]:text-primary-dark disabled:border-grey-400 disabled:text-text-placeholder"
            },
            {
                variant: "outlined",
                color: "secondary",
                className:
                    "border-secondary-main text-secondary-main hover:border-secondary-dark hover:text-secondary-dark data-[interaction=hover]:border-secondary-dark data-[interaction=hover]:text-secondary-dark disabled:border-grey-400 disabled:text-text-placeholder"
            },
            {
                variant: "outlined",
                color: "grey",
                className:
                    "border-grey-400 text-text-disabled hover:border-grey-500 hover:text-text-secondary data-[interaction=hover]:border-grey-500 data-[interaction=hover]:text-text-secondary disabled:border-grey-400 disabled:text-text-placeholder"
            },
            // Text variants
            {
                variant: "text",
                color: "primary",
                className:
                    "text-primary-main hover:text-primary-main data-[interaction=hover]:text-primary-main disabled:text-text-placeholder"
            },
            {
                variant: "text",
                color: "secondary",
                className:
                    "text-secondary-main hover:text-secondary-main data-[interaction=hover]:text-secondary-main disabled:text-text-placeholder"
            },
            {
                variant: "text",
                color: "grey",
                className:
                    "text-text-disabled hover:text-text-disabled data-[interaction=hover]:text-text-disabled disabled:text-text-placeholder"
            },
            // Background variants
            {
                variant: "background",
                color: "primary",
                className:
                    "text-primary-main hover:bg-primary-hover data-[interaction=hover]:bg-primary-hover disabled:text-text-placeholder"
            },
            {
                variant: "background",
                color: "secondary",
                className:
                    "text-secondary-main hover:bg-secondary-hover data-[interaction=hover]:bg-secondary-hover disabled:text-text-placeholder"
            },
            {
                variant: "background",
                color: "grey",
                className:
                    "text-text-secondary hover:bg-grey-a100 data-[interaction=hover]:bg-grey-a100 disabled:text-text-placeholder"
            },
            // Archive button variants
            {
                variant: "archive-button",
                color: "primary",
                className:
                    "bg-common-white-main text-grey-300 dark:bg-common-white-main"
            },
            {
                variant: "archive-button",
                color: "secondary",
                className: "bg-common-white-main dark:bg-common-white-main"
            },
            {
                variant: "archive-button",
                color: "grey",
                className: "bg-common-white-main dark:bg-common-white-main"
            }
        ],
        defaultVariants: {
            variant: "filled",
            color: "primary",
            size: "medium"
        }
    }
);

// Helper function to map button size to IconBox size
function getIconBoxSize(
    size:
        | "xl"
        | "large"
        | "medium"
        | "small"
        | "icon-xl"
        | "icon-large"
        | "icon-medium"
        | "icon-small"
        | null
        | undefined
): "xl" | "large" | "medium" | "small" | "extraSmall" {
    if (!size) return "medium";
    if (size.startsWith("icon-")) {
        const iconSize = size.replace("icon-", "") as
            | "xl"
            | "large"
            | "medium"
            | "small";
        return iconSize === "xl"
            ? "xl"
            : iconSize === "large"
              ? "large"
              : iconSize === "medium"
                ? "medium"
                : "small";
    }
    return size === "xl"
        ? "xl"
        : size === "large"
          ? "large"
          : size === "medium"
            ? "medium"
            : size === "small"
              ? "small"
              : "medium";
}

// Helper function to get icon color class based on variant and color
function getIconColorClass(
    variant: "filled" | "outlined" | "text" | "background" | "archive-button",
    color: "primary" | "secondary" | "grey",
    isDisabled: boolean
): string {
    if (isDisabled) {
        return "text-text-placeholder";
    }

    if (variant === "archive-button") {
        return "text-grey-300";
    }

    if (variant === "filled") {
        if (color === "primary") {
            return "text-common-white-main dark:text-common-white-main";
        }
        if (color === "secondary") {
            return "text-common-white-main dark:text-common-black-main";
        }
        if (color === "grey") {
            return "text-text-secondary dark:text-text-secondary";
        }
    }

    if (variant === "outlined") {
        if (color === "primary") {
            return "text-primary-main";
        }
        if (color === "secondary") {
            return "text-secondary-main";
        }
        if (color === "grey") {
            return "text-text-disabled";
        }
    }

    if (variant === "text") {
        if (color === "primary") {
            return "text-primary-main";
        }
        if (color === "secondary") {
            return "text-secondary-main";
        }
        if (color === "grey") {
            return "text-text-disabled";
        }
    }

    if (variant === "background") {
        if (color === "primary") {
            return "text-primary-main";
        }
        if (color === "secondary") {
            return "text-secondary-main";
        }
        if (color === "grey") {
            return "text-text-secondary";
        }
    }

    return "";
}

export interface ButtonProps
    extends
        Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    startIcon?: IconName;
    endIcon?: IconName;
    interaction?: "hover";
    loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            color,
            size,
            asChild = false,
            startIcon,
            endIcon,
            interaction,
            loading = false,
            disabled = false,
            children,
            ...props
        },
        ref
    ) => {
        const Comp = asChild ? Slot : "button";
        const isDisabled = disabled || loading;
        const resolvedVariant = variant || "filled";
        const resolvedColor = (color || "primary") as
            | "primary"
            | "secondary"
            | "grey";
        const iconColorClass = getIconColorClass(
            resolvedVariant,
            resolvedColor,
            isDisabled
        );

        return (
            <Comp
                className={cn(
                    buttonVariants({ variant, color: resolvedColor, size }),
                    isDisabled && "cursor-not-allowed",
                    className
                )}
                ref={ref}
                type="button"
                disabled={isDisabled}
                data-interaction={interaction}
                {...props}
            >
                {loading && (
                    <Spinner
                        size={
                            size === "xl" || size === "icon-xl"
                                ? "large"
                                : size === "small" || size === "icon-small"
                                  ? "small"
                                  : "medium"
                        }
                        className="mr-0.5"
                    />
                )}
                {startIcon && !loading && (
                    <IconBox
                        name={startIcon}
                        size={getIconBoxSize(size)}
                        className={cn("-ml-0.5", iconColorClass)}
                    />
                )}
                {children}
                {endIcon && !loading && (
                    <IconBox
                        name={endIcon}
                        size={getIconBoxSize(size)}
                        className={cn("-mr-0.5", iconColorClass)}
                    />
                )}
            </Comp>
        );
    }
);
Button.displayName = "Button";

export { Button, buttonVariants };
